import axios, { AxiosInstance, InternalAxiosRequestConfig } from "axios";
import { useAuthStore } from "@/stores/auth";
import { useLoaderStore } from "@/stores/loader";
import Swal from "sweetalert2";
import type { Router } from "vue-router";

const showErrorToast = (title: string) => {
  Swal.fire({
    toast: true,
    position: "top-end",
    icon: "error",
    title,
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
  });
};

export interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  showLoader?: boolean;
  skipAuthRedirect?: boolean;
  keyRequest?: boolean;
  responseType?: InternalAxiosRequestConfig["responseType"];
}

let routerInstance: Router | null = null;

export const setRouter = (router: Router) => {
  routerInstance = router;
};


// Helper function to create configured instance
const createAxiosInstance = (baseURL: string): AxiosInstance => {
  const instance = axios.create({
    baseURL,
    timeout: 30000,
  });


  // Add request interceptor
  instance.interceptors.request.use(
    async (config: CustomAxiosRequestConfig) => {
      const loaderStore = useLoaderStore();
      const { getToken, fetchDemoAPIKey } = useAuthStore()

      if (config.keyRequest) {
        const apiKey = await fetchDemoAPIKey()

        config.headers["Content-Type"] = "application/json";
        config.headers["X-Api-Key"] = apiKey;
      } else {
      // Skip token addition for login/refresh endpoints
      if (config.url?.includes("/login") || config.url?.includes("/refresh")) {
        if (config.showLoader !== false) {
          loaderStore.startRequest();
        }
        return config;
      }

        const token = await getToken()

        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        } else {
          routerInstance?.push("/");         
        }
      }

      if (config.showLoader !== false) {
        loaderStore.startRequest();
      }
      return config;
    },
    (error) => {
      const loaderStore = useLoaderStore();
      loaderStore.endRequest();
      return Promise.reject(error);
    }
  );

  // Add response interceptor
  instance.interceptors.response.use(
    (response) => {
      const loaderStore = useLoaderStore();
      if ((response.config as CustomAxiosRequestConfig).showLoader !== false) {
        loaderStore.endRequest();
      }
      return response;
    },
    (error) => {
      const loaderStore = useLoaderStore();
      const authStore = useAuthStore();

      if ((error.config as CustomAxiosRequestConfig)?.showLoader !== false) {
        loaderStore.endRequest();
      }

      switch (error.status) {
        case 400:
          if (error.response.data) {
            const modelStateErrors = [];
            for (const item of error.response.data) {
              if (item) {
                modelStateErrors.push(item?.description);
              }
            }
            throw modelStateErrors.flat();
          } else {
            showErrorToast(`${error.error}`);
          }
          break;
        case 401:
          authStore.clearToken();
          if (!(error.config as CustomAxiosRequestConfig)?.skipAuthRedirect) {
            routerInstance?.push("/");
          }
          break;
        case 404:
          routerInstance?.push("/not-found");
          break;
        case 500:
          showErrorToast(`${error.error}`);
          break;
        default:
          showErrorToast(
            `Connection issue detected. Please verify your internet connection.`
          );
          break;
      }
      return Promise.reject(error);
    }
  );

  return instance;
};

export const authApi = createAxiosInstance(process.env.VUE_APP_CLIENT_URL);
export const mdaApi = createAxiosInstance(process.env.VUE_APP_MDA_URL);

export default authApi;